import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Button,
  Paper,
  Group,
  Stack,
  SimpleGrid,
  Box,
  Badge,
  Center,
  Divider,
  List,
  ThemeIcon,
  Anchor
} from '@mantine/core';
import { IconCheck, IconUsers, IconBrain, IconBell, IconSparkles, IconTrendingUp, IconShieldCheck } from '@tabler/icons-react';
import BetaSignupModal from '../components/BetaSignupModal';

const LandingPage = () => {
  const navigate = useNavigate();
  const [showBetaModal, setShowBetaModal] = useState(false);

  return (
    <Box>
      {/* Hero Section */}
      <Box
        style={{
          background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)',
          minHeight: '90vh',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Container size="lg" py="xl">
          <Stack align="center" gap="xl">
            {/* Logo/Brand */}
            <Text
              size="80px"
              style={{ lineHeight: 1 }}
            >
              🎁
            </Text>

            {/* Main Headline */}
            <Stack align="center" gap="md">
              <Title
                order={1}
                size="60px"
                ta="center"
                style={{
                  background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 800
                }}
              >
                GiftPersona
              </Title>
              <Title order={2} size="36px" ta="center" fw={600}>
                Never Give a Bad Gift Again
              </Title>
              <Text size="xl" c="dimmed" ta="center" maw={600}>
                Stop guessing. Start giving gifts people actually love.
                AI-powered recommendations based on real personality profiles.
              </Text>
            </Stack>

            {/* CTA Buttons — beta-request form is the only signup path */}
            <Group gap="md" mt="md">
              <Button
                size="xl"
                variant="gradient"
                gradient={{ from: 'grape', to: 'pink' }}
                onClick={() => setShowBetaModal(true)}
                leftSection={<IconSparkles size={20} />}
              >
                Join Beta Waitlist - It's Free
              </Button>
              <Button
                size="xl"
                variant="outline"
                color="grape"
                onClick={() => navigate('/login')}
              >
                Sign In
              </Button>
            </Group>

            <Badge
              size="lg"
              variant="light"
              color="grape"
              radius="xl"
              leftSection="🎉"
            >
              Join the closed beta - Limited spots available
            </Badge>
          </Stack>
        </Container>
      </Box>

      {/* Value Proposition */}
      <Container size="lg" py={80}>
        <Stack align="center" gap="xl">
          <Title order={2} ta="center" size="40px">
            Tired of gift-giving stress?
          </Title>

          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="xl" mt="xl">
            <Paper p="xl" radius="md" withBorder ta="center">
              <Text size="60px" mb="md">😰</Text>
              <Text fw={600} mb="xs">Forgetting Birthdays</Text>
              <Text size="sm" c="dimmed">
                Never get caught off guard again
              </Text>
            </Paper>

            <Paper p="xl" radius="md" withBorder ta="center">
              <Text size="60px" mb="md">🤔</Text>
              <Text fw={600} mb="xs">Not Knowing What to Buy</Text>
              <Text size="sm" c="dimmed">
                AI suggests perfect gifts
              </Text>
            </Paper>

            <Paper p="xl" radius="md" withBorder ta="center">
              <Text size="60px" mb="md">😅</Text>
              <Text fw={600} mb="xs">Gifts That Collect Dust</Text>
              <Text size="sm" c="dimmed">
                Based on real preferences
              </Text>
            </Paper>

            <Paper p="xl" radius="md" withBorder ta="center">
              <Text size="60px" mb="md">💸</Text>
              <Text fw={600} mb="xs">Overspending</Text>
              <Text size="sm" c="dimmed">
                Find perfect gifts at any budget
              </Text>
            </Paper>
          </SimpleGrid>
        </Stack>
      </Container>

      {/* Features */}
      <Box style={{ background: 'rgba(102,126,234,0.03)' }} py={80}>
        <Container size="lg">
          <Stack align="center" gap="xl">
            <Title order={2} ta="center" size="40px">
              How GiftPersona Works
            </Title>

            <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xl" mt="xl">
              {/* Feature 1 */}
              <Paper p="xl" radius="lg" withBorder>
                <Stack gap="md">
                  <Center>
                    <ThemeIcon size={60} radius="xl" variant="gradient" gradient={{ from: 'grape', to: 'pink' }}>
                      <IconUsers size={30} />
                    </ThemeIcon>
                  </Center>
                  <Title order={3} ta="center">Build Profiles</Title>
                  <Text c="dimmed" ta="center">
                    Create detailed personas for friends and family. Track preferences, interests, and personality traits.
                  </Text>
                </Stack>
              </Paper>

              {/* Feature 2 */}
              <Paper p="xl" radius="lg" withBorder>
                <Stack gap="md">
                  <Center>
                    <ThemeIcon size={60} radius="xl" variant="gradient" gradient={{ from: 'blue', to: 'cyan' }}>
                      <IconBell size={30} />
                    </ThemeIcon>
                  </Center>
                  <Title order={3} ta="center">Set Reminders</Title>
                  <Text c="dimmed" ta="center">
                    Never forget another birthday or special occasion. Smart notifications keep you ahead of the game.
                  </Text>
                </Stack>
              </Paper>

              {/* Feature 3 */}
              <Paper p="xl" radius="lg" withBorder>
                <Stack gap="md">
                  <Center>
                    <ThemeIcon size={60} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
                      <IconBrain size={30} />
                    </ThemeIcon>
                  </Center>
                  <Title order={3} ta="center">Get AI Suggestions</Title>
                  <Text c="dimmed" ta="center">
                    Our AI analyzes personalities and suggests perfect gifts. From trendy to timeless, we've got you covered.
                  </Text>
                </Stack>
              </Paper>
            </SimpleGrid>
          </Stack>
        </Container>
      </Box>

      {/* Pricing */}
      <Container size="lg" py={80}>
        <Stack align="center" gap="xl">
          <Title order={2} ta="center" size="40px">
            Choose Your Plan
          </Title>
          <Text size="lg" c="dimmed" ta="center" maw={600}>
            Start free and upgrade anytime. Beta testers get 50% off Pro for life!
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" mt="xl" style={{ width: '100%', maxWidth: 900 }}>
            {/* Free Plan */}
            <Paper p="xl" radius="lg" withBorder>
              <Stack gap="lg">
                <div>
                  <Badge size="lg" variant="light" color="blue" mb="md">FREE</Badge>
                  <Title order={2}>GiftPersona Free</Title>
                  <Text size="sm" c="dimmed" mt="xs">Perfect for getting started</Text>
                </div>

                <div>
                  <Text size="40px" fw={700}>$0</Text>
                  <Text size="sm" c="dimmed">Forever free</Text>
                </div>

                <Divider />

                <List
                  spacing="sm"
                  icon={
                    <ThemeIcon size={20} radius="xl" color="blue">
                      <IconCheck size={14} />
                    </ThemeIcon>
                  }
                >
                  <List.Item>Unlimited basic searches</List.Item>
                  <List.Item>3 personas</List.Item>
                  <List.Item>20 saved items</List.Item>
                  <List.Item>3 groups</List.Item>
                  <List.Item>Basic reminders</List.Item>
                  <List.Item>Basic AI suggestions</List.Item>
                  <List.Item>Trending gifts</List.Item>
                </List>

                <Button
                  fullWidth
                  size="lg"
                  variant="light"
                  color="blue"
                  onClick={() => setShowBetaModal(true)}
                >
                  Join Beta - Free
                </Button>
              </Stack>
            </Paper>

            {/* Pro Plan */}
            <Paper
              p="xl"
              radius="lg"
              withBorder
              style={{
                border: '2px solid',
                borderColor: '#667eea',
                position: 'relative'
              }}
            >
              <Badge
                size="lg"
                variant="gradient"
                gradient={{ from: 'grape', to: 'pink' }}
                style={{ position: 'absolute', top: -15, right: 20 }}
              >
                🔥 POPULAR
              </Badge>

              <Stack gap="lg">
                <div>
                  <Badge size="lg" variant="gradient" gradient={{ from: 'grape', to: 'pink' }} mb="md">PRO</Badge>
                  <Title order={2}>GiftPersona Pro</Title>
                  <Text size="sm" c="dimmed" mt="xs">For serious gift-givers</Text>
                </div>

                <div>
                  <Group align="baseline" gap="xs">
                    <Text size="40px" fw={700}>$9.99</Text>
                    <Text size="lg" c="dimmed">/month</Text>
                  </Group>
                  <Badge size="sm" variant="light" color="green" mt="xs">
                    Beta: 50% off forever!
                  </Badge>
                </div>

                <Divider />

                <List
                  spacing="sm"
                  icon={
                    <ThemeIcon size={20} radius="xl" variant="gradient" gradient={{ from: 'grape', to: 'pink' }}>
                      <IconCheck size={14} />
                    </ThemeIcon>
                  }
                >
                  <List.Item><Text fw={600}>Everything in Free, plus:</Text></List.Item>
                  <List.Item>🔥 Unlimited personas</List.Item>
                  <List.Item>🔥 Deep AI persona builder</List.Item>
                  <List.Item>🔥 AI gift concierge</List.Item>
                  <List.Item>🔥 Unlimited groups</List.Item>
                  <List.Item>🔥 Anonymous gifting</List.Item>
                  <List.Item>🔥 Price drop alerts</List.Item>
                  <List.Item>🔥 Smart reminders</List.Item>
                  <List.Item>🔥 Marketplace integrations</List.Item>
                  <List.Item>🔥 Unlimited wishlists</List.Item>
                  <List.Item>🔥 Priority support</List.Item>
                </List>

                <Button
                  fullWidth
                  size="lg"
                  variant="gradient"
                  gradient={{ from: 'grape', to: 'pink' }}
                  onClick={() => setShowBetaModal(true)}
                >
                  Join Beta - Pro
                </Button>
              </Stack>
            </Paper>
          </SimpleGrid>
        </Stack>
      </Container>

      {/* Beta CTA */}
      <Box style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }} py={80}>
        <Container size="md">
          <Stack align="center" gap="xl">
            <Title order={2} ta="center" c="white" size="40px">
              🚀 Currently in Closed Beta
            </Title>
            <Text size="xl" c="white" ta="center" maw={700}>
              We're accepting a limited number of beta testers to help us perfect 
              the gift-giving experience. Join the waitlist to get early access!
            </Text>

            <Paper p="xl" radius="lg" style={{ width: '100%', maxWidth: 500 }}>
              <Stack gap="md">
                <Title order={3} ta="center">✨ Beta Testers Get:</Title>
                <List
                  spacing="sm"
                  icon={
                    <ThemeIcon size={20} radius="xl" color="green">
                      <IconCheck size={14} />
                    </ThemeIcon>
                  }
                >
                  <List.Item>Lifetime Pro discount (50% off)</List.Item>
                  <List.Item>Early access to new features</List.Item>
                  <List.Item>Direct line to the founders</List.Item>
                  <List.Item>Shape the product roadmap</List.Item>
                </List>

                <Button
                  fullWidth
                  size="xl"
                  variant="gradient"
                  gradient={{ from: 'orange', to: 'red' }}
                  onClick={() => setShowBetaModal(true)}
                  mt="md"
                >
                  Request Beta Access
                </Button>
              </Stack>
            </Paper>
          </Stack>
        </Container>
      </Box>

      {/* Footer */}
      <Box style={{ background: '#1a1b1e', color: 'white' }} py={40}>
        <Container size="lg">
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xl">
            <div>
              <Text size="lg" fw={700} mb="md">🎁 GiftPersona</Text>
              <Text size="sm" c="dimmed">
                AI-powered gift recommendations for meaningful giving.
              </Text>
            </div>

            <div>
              <Text size="sm" fw={700} mb="md">Product</Text>
              <Stack gap="xs">
                <Anchor size="sm" c="dimmed" onClick={() => setShowBetaModal(true)} style={{ cursor: 'pointer' }}>
                  Join Beta
                </Anchor>
                <Anchor size="sm" c="dimmed" onClick={() => navigate('/login')} style={{ cursor: 'pointer' }}>
                  Sign In
                </Anchor>
              </Stack>
            </div>

            <div>
              <Text size="sm" fw={700} mb="md">Legal</Text>
              <Stack gap="xs">
                <Text size="sm" c="dimmed">Privacy Policy</Text>
                <Text size="sm" c="dimmed">Terms of Service</Text>
              </Stack>
            </div>
          </SimpleGrid>

          <Divider my="xl" color="dark" />

          <Text size="sm" c="dimmed" ta="center">
            © 2025 GiftPersona. All rights reserved.
          </Text>
        </Container>
      </Box>

      <BetaSignupModal opened={showBetaModal} onClose={() => setShowBetaModal(false)} />
    </Box>
  );
};

export default LandingPage;
